/**
 * How many personal analyses the trader has left.
 *
 * ## Why it shows analyses and not credits
 *
 * "412 credits" means nothing to anyone. "8 left" is the same fact in the unit
 * the user actually spends in, and it is the number they need before deciding
 * whether to run one. The credit figure is in the tooltip for anyone who wants
 * to reconcile against their history.
 *
 * ## Why it is quiet until it matters
 *
 * A permanent counter is a permanent nag. This renders neutrally until the
 * balance is low, warns at one remaining, and only then invites a top-up —
 * which is also the only moment the invitation is useful rather than an
 * upsell for its own sake.
 */
import { useEffect, useState } from 'react';
import { Link } from '../lib/router.js';
import type { CreditsResponse } from '@aegis/api-client';

/**
 * How loud the meter should be.
 *
 * Extracted and exported because it is the only decision in this component,
 * and the repo tests decisions rather than renders — same reason
 * `chart-redraw.test.ts` covers the branch and not Lightweight Charts.
 */
export function meterState(analysesRemaining: number): 'ok' | 'low' | 'empty' {
  if (analysesRemaining <= 0) return 'empty';
  return analysesRemaining <= 1 ? 'low' : 'ok';
}

/** "8 analyses left" / "1 analysis left" — the unit the user spends in. */
export function meterLabel(analysesRemaining: number): string {
  return analysesRemaining === 1 ? 'analysis left' : 'analyses left';
}

export interface CreditMeterProps {
  readonly load: () => Promise<CreditsResponse>;
  /** Bumped by the caller after a spend, so the meter re-reads. */
  readonly version?: number;
}

export function CreditMeter({ load, version = 0 }: CreditMeterProps) {
  const [credits, setCredits] = useState<CreditsResponse | null>(null);

  useEffect(() => {
    let live = true;
    load()
      .then((c) => { if (live) setCredits(c); })
      // Silent. A credit balance that fails to load must not put an error in
      // the header of a trading app — the chart is what matters and this is
      // furniture.
      .catch(() => { if (live) setCredits(null); });
    return () => { live = false; };
  }, [load, version]);

  if (!credits) return null;

  const left = credits.analysesRemaining;
  const state = meterState(left);

  return (
    <span
      className={`credit-meter credit-${state}`}
      title={
        `${credits.total} credits — ${credits.granted} from your ${credits.plan} plan `
        + `this period, ${credits.purchased} purchased. `
        + `Each personal analysis costs ${credits.analysisCost}.`
      }
    >
      <span className="credit-count">{left}</span>
      <span className="credit-label">
        {meterLabel(left)}
      </span>
      {state !== 'ok' && (
        <Link to="/pricing" className="credit-topup">Top up</Link>
      )}
    </span>
  );
}
